// =============================================================================
// FILE: packages/renderer/src/services/growthAgent.ts
// PURPOSE: Emmy Growth Agent — maps every platform action to a positive,
//          value-focused celebration message. Emmy doesn't say "Task complete";
//          she highlights the business impact of what R² just did.
// =============================================================================

export type GrowthEventType =
  | 'project_created' | 'project_updated'
  | 'asset_added'     | 'building_added'  | 'rack_created'
  | 'clash_resolved'  | 'document_filed'  | 'file_ingested'
  | 'meeting_captured'| 'connection_created' | 'contact_added'
  | 'generic_win';

export interface GrowthCelebration {
  id:          string;
  tier:        'milestone' | 'progress' | 'routine';
  headline:    string;
  message:     string;
  value:       string;
  accentColor: string;
}

type MessageTemplate = Omit<GrowthCelebration, 'id'>;

const MESSAGES: Record<GrowthEventType, MessageTemplate> = {
  project_created: {
    tier: 'milestone',
    headline: 'New project is live!',
    message: "I've registered your project and it's ready for data.",
    value: 'Every great telecom build starts with a clean scope. You\'re already ahead.',
    accentColor: '#25B5A8',
  },
  project_updated: {
    tier: 'progress',
    headline: 'Project milestone advanced.',
    message: "I've synced the latest changes to your project record.",
    value: 'Keeping records current reduces rework risk and strengthens your billing narrative.',
    accentColor: '#2DD4BF',
  },
  asset_added: {
    tier: 'progress',
    headline: 'Device added to inventory.',
    message: "I've registered the new asset in your digital twin.",
    value: 'More devices = higher fidelity model. Your client is getting a better deliverable.',
    accentColor: '#7AE0D6',
  },
  building_added: {
    tier: 'progress',
    headline: 'Building added to the model.',
    message: "I've extended your spatial model with a new building.",
    value: 'Each building brings your digital twin closer to 100% site coverage.',
    accentColor: '#38BDF8',
  },
  rack_created: {
    tier: 'progress',
    headline: 'Rack configured.',
    message: "I've registered a new rack in your infrastructure model.",
    value: 'Accurate rack data means better capacity planning — and better client confidence.',
    accentColor: '#34D399',
  },
  clash_resolved: {
    tier: 'milestone',
    headline: 'Clash resolved!',
    message: "I've confirmed design integrity — no conflicts in this zone.",
    value: 'This proactive resolution saves an estimated 2–4 hours of rework on site.',
    accentColor: '#6EE7B7',
  },
  document_filed: {
    tier: 'routine',
    headline: 'Document registered.',
    message: "I've filed that to your document register.",
    value: 'Solid documentation keeps every project auditable and every client confident.',
    accentColor: '#F9A8D4',
  },
  file_ingested: {
    tier: 'milestone',
    headline: 'R² processed your file!',
    message: "Smart Ingestion Engine extracted and structured the key data automatically.",
    value: 'Automated ingestion just saved you an estimated 20–30 minutes of manual entry.',
    accentColor: '#A78BFA',
  },
  meeting_captured: {
    tier: 'progress',
    headline: 'Meeting captured.',
    message: "I've saved and logged your session notes.",
    value: 'Every captured meeting is a billable event you can reference and invoice.',
    accentColor: '#7AE0D6',
  },
  connection_created: {
    tier: 'progress',
    headline: 'Connection mapped.',
    message: "I've added a new link to your cabling graph.",
    value: 'More connections means higher-fidelity twin. Your model is getting stronger.',
    accentColor: '#34D399',
  },
  contact_added: {
    tier: 'routine',
    headline: 'Contact onboarded.',
    message: "I've added them to your project contact registry.",
    value: 'A complete contact list cuts communication delays later in the project lifecycle.',
    accentColor: '#818CF8',
  },
  generic_win: {
    tier: 'routine',
    headline: 'Action recorded.',
    message: "I've processed and saved that action.",
    value: 'Every action you take here is building a stronger, more defensible deliverable.',
    accentColor: '#25B5A8',
  },
};

export function getGrowthCelebration(event: GrowthEventType): GrowthCelebration {
  const template = MESSAGES[event] ?? MESSAGES.generic_win;
  return {
    ...template,
    id: `gc-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
  };
}

/** Maps an audit trail entry (tableName + operation) to a GrowthEventType */
export function inferEventType(tableName: string, operation: string): GrowthEventType | null {
  const op = operation.toUpperCase();
  if (op === 'INSERT') {
    switch (tableName) {
      case 'projects':          return 'project_created';
      case 'assets':            return 'asset_added';
      case 'buildings':         return 'building_added';
      case 'racks':             return 'rack_created';
      case 'clash_results':     return 'clash_resolved';
      case 'document_register': return 'document_filed';
      case 'meetings':          return 'meeting_captured';
      case 'connections':       return 'connection_created';
      case 'contacts':          return 'contact_added';
      default:                  return null;
    }
  }
  if (op === 'UPDATE' && tableName === 'projects') return 'project_updated';
  return null;
}
